import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ThreeDotsVertical,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import {
  type DocumentDetail,
  DocumentStatus,
  DocumentSubType,
  Source,
} from "@/briefpoint-client";
import { useDocumentApi } from "@/hooks/useApi";
import { getArchivedDocumentListOptions } from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type { DocumentClient } from "@/lib/types";
import {
  addUploadDate,
  getDocumentStatusText,
  getDocumentSubTypeName,
  toLocaleUSDateString,
} from "@/lib/utils";
import ConfirmDeleteDocumentModal from "../ConfirmDeleteDocumentModal";
import { DocumentIcon } from "../DocumentIcon";
import DocumentListTableSupplementalRow from "../DocumentListTable/DocumentListTableSupplementalRow";
import Loading from "../Loading";
import Pagination from "../Pagination/Pagination";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "../ui/toast";
import styles from "./ArchiveDocumentList.module.scss";

export default function ArchiveDocumentList() {
  const documentApi = useDocumentApi();
  const [documentToDelete, setDocumentToDelete] = useState<
    DocumentClient | undefined
  >(undefined);

  const archivedDocumentsQuery = useQuery({
    ...getArchivedDocumentListOptions(documentApi),
    select: (data) => {
      const docs = data
        .map((document) => addUploadDate(document as DocumentDetail))
        .filter((d) => d.status !== DocumentStatus.Error);

      return {
        documents: docs.filter((doc) => !doc.parentDocumentId),
        supplementalDocuments: docs.filter((doc) => doc.parentDocumentId),
      };
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (document: DocumentClient) => {
      await documentApi.documentUpdateDocument({
        documentId: document.id,
        document: {
          ...document,
          isArchived: false,
        },
      });
      return document;
    },

    onSuccess: (document: DocumentClient) => {
      toast.success(`${document.name} was restored`);
    },
    onError: (document: DocumentDetail) => {
      toast.error(`Error restoring ${document.name}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries(
        getArchivedDocumentListOptions(documentApi),
      );
    },
  });

  /* biome-ignore lint/correctness/useExhaustiveDependencies: we want to re-run the effect when the columns change */
  const columns: ColumnDef<DocumentDetail>[] = useMemo(
    () => [
      {
        header: "",
        accessorKey: "icon",
        accessorFn: (row) => row.source,
        meta: {
          width: "20px",
        },
        cell: ({ row }) => {
          return (
            <DocumentIcon
              extension={
                row.original.source === Source.InternalGenerated
                  ? "docx"
                  : "pdf"
              }
            />
          );
        },
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Document Name
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "name",
        accessorFn: (row) => row.name,
        meta: {
          width: "35%",
        },
        cell: ({ row }) => {
          return (
            <div data-testid="document-name" className={styles.cellData}>
              {row.original.name}
            </div>
          );
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Case Number
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "caseNumber",
        accessorFn: (row) => row.info?.caseNumber ?? "-",
        meta: {
          width: "20%",
        },
        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {row.original.info?.caseNumber ?? "-"}
            </div>
          );
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Document Type
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "documentType",
        accessorFn: (row) =>
          getDocumentSubTypeName(
            row.info?.documentSubType ?? DocumentSubType.Unknown,
            row.info?.jurisdiction ?? "",
          ),

        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {row.original.info?.documentSubType
                ? getDocumentSubTypeName(
                    row.original.info.documentSubType,
                    row.original.info.jurisdiction ?? "",
                  )
                : "Unknown"}
            </div>
          );
        },
        meta: {
          width: "20%",
        },
        sortingFn: "text",
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Upload Date
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "uploadDate",
        accessorFn: (row) => row.uploadDate?.getTime(),
        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {row.original.uploadDate
                ? toLocaleUSDateString(row.original.uploadDate)
                : "-"}
            </div>
          );
        },
        meta: {
          width: "13%",
        },
      },
      {
        header: ({ column }) => {
          return (
            <Button
              className={styles.fieldHeaderButton}
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Status{" "}
              {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
            </Button>
          );
        },
        accessorKey: "status",
        accessorFn: (row) => getDocumentStatusText(row.status).status,
        cell: ({ row }) => {
          return (
            <div className={styles.cellData}>
              {getDocumentStatusText(row.original.status).status}
            </div>
          );
        },
        meta: {
          width: "12%",
        },
      },
      {
        header: "",
        accessorKey: "actions",
        cell: ({ row }) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ThreeDotsVertical size={14} color="var(--text)" />
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() =>
                      restoreMutation.mutate(row.original as DocumentClient)
                    }
                  >
                    Restore
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-danger"
                    onClick={() =>
                      setDocumentToDelete(row.original as DocumentClient)
                    }
                  >
                    Permanently Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          );
        },
        meta: {
          width: "20px",
        },
      },
    ],
    [documentToDelete],
  );

  const table = useReactTable({
    data: archivedDocumentsQuery.data?.documents ?? [],
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  /* optimistically update the documents list */
  return (
    <Loading isLoading={archivedDocumentsQuery.isLoading}>
      <ConfirmDeleteDocumentModal
        showDeleteConfirm={!!documentToDelete}
        setShowDeleteConfirm={(show) =>
          setDocumentToDelete(show ? documentToDelete : undefined)
        }
        document={documentToDelete as DocumentClient}
      />
      {archivedDocumentsQuery.data?.documents.length ? (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody data-testid="archived-document-list-body">
              {table.getRowModel().rows.map((row) => (
                <>
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: (
                            cell.column.columnDef.meta as { width: string }
                          )?.width, // taken from column meta definition
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {archivedDocumentsQuery.data?.supplementalDocuments
                    ?.filter((doc) => doc.parentDocumentId === row.original.id)
                    .sort(
                      (a, b) => a.uploadDate.getTime() - b.uploadDate.getTime(),
                    )
                    .map((doc) => (
                      <DocumentListTableSupplementalRow
                        key={doc.id}
                        archived
                        row={row}
                        document={doc}
                      />
                    ))}
                </>
              ))}
            </TableBody>
          </Table>
          <div className="d-flex justify-content-end pt-2">
            <Pagination
              navigate={(page) => table.setPageIndex(page - 1)}
              currentPage={table.getState().pagination.pageIndex + 1}
              totalPages={table.getPageCount()}
              totalItemCount={
                archivedDocumentsQuery.data?.documents.length ?? 0
              }
              itemsPerPage={table.getState().pagination.pageSize}
              showPageCount
              showInput
            />
          </div>
        </>
      ) : (
        <div>
          You have no archived documents. Please upload a document
          <Link className="ps-1" to="/documents">
            here
          </Link>
        </div>
      )}
    </Loading>
  );
}
